// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
import { UserAdd } from 'flowbite-react-icons/outline'

import { withStopPropagation } from './cardSurface'
import { FormCard, StatusPill } from './FormCard'

export interface TestOption {
  title: string
  description: string
}

export interface TestOptionsCardProps {
  /** Display name of the selected sequencing test (first grid cell title). */
  testTypeLabel: string
  /** Turnaround line under the test name (e.g. “Typically 3 calendar days after sample receipt”). */
  turnaroundDescription: string
  /** Optional third grid cell used on pre-submit pages to show comparator sample copy. Omit to hide the tile. */
  familySamples?: TestOption
  sampleType?: 'whole-blood' | 'buccal-swab' | 'saliva'
  /** Optional fourth grid cell (e.g. incidental findings / research opt-in) when product supplies copy. */
  secondaryDisclosure?: TestOption
  onEdit?: () => void
  /**
   * Secondary header action: jump straight to the Add Comparator flow in one
   * click. Rendered as a lower-emphasis outlined pill left of Edit, mirroring
   * the Indications card's "Add Phenotypes" action.
   */
  onAddComparator?: () => void
  /**
   * When true, the Add Comparator button renders in a disabled state instead of
   * being interactive (e.g. the comparator cap is reached or editing is not
   * currently allowed).
   */
  addComparatorDisabled?: boolean
  /** Optional tooltip/title explaining why Add Comparator is disabled. */
  addComparatorDisabledReason?: string
  /** When false, shows Figma empty state + info pill and purple left rail (needsAttention). */
  hasSelectedTestType?: boolean
  /** e.g. Duo test chosen but neither biological parent comparator is selected yet. */
  extraNeedsAttention?: boolean
  /** Shown when nothing is selected (Figma: "None entered"). */
  emptyStateText?: string
  /** Success status pill label (draft flow defaults to “Complete”; post-submit pages may use “Sent to lab”). */
  statusSuccessText?: string
  /**
   * R5 (side-nav) layout: the card shows only sequencing speed + sample type.
   * Comparators are a separate card, so the family/secondary tiles and
   * comparator-related attention are suppressed here and the copy drops any
   * comparator references.
   */
  r5SpeedAndSampleOnly?: boolean
  /** R5: required section — show the purple left accent bar even when complete. */
  required?: boolean
}

function SequencingGridCell({ title, description }: TestOption) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <p className="text-base font-medium leading-6 text-text-heading dark:text-white">{title}</p>
      <p className="text-xs font-medium leading-5 text-text-body dark:text-gray-400">{description}</p>
    </div>
  )
}

export function TestOptionsCard({
  testTypeLabel,
  turnaroundDescription,
  familySamples,
  sampleType,
  secondaryDisclosure,
  onEdit,
  onAddComparator,
  addComparatorDisabled = false,
  addComparatorDisabledReason,
  hasSelectedTestType = true,
  extraNeedsAttention = false,
  emptyStateText = 'None entered',
  statusSuccessText = 'Complete',
  r5SpeedAndSampleOnly = false,
  required = false,
}: TestOptionsCardProps) {
  const sampleTypeMissing = sampleType == null
  const sampleTypeDisplay = sampleTypeMissing
    ? 'Sample type (required)'
    : sampleType === 'buccal-swab'
      ? 'Buccal swab'
      : sampleType === 'saliva'
        ? 'Saliva'
        : 'Whole blood'
  const sampleTypeDescription = sampleTypeMissing
    ? 'Choose a sample type in sequencing options.'
    : 'Sample type'

  // R5 shows only sequencing speed + sample type; comparators are a separate
  // card, so the family/secondary tiles and comparator attention don't apply.
  const familySamplesCell = r5SpeedAndSampleOnly ? undefined : familySamples
  const secondaryDisclosureCell = r5SpeedAndSampleOnly ? undefined : secondaryDisclosure
  const effectiveExtraNeedsAttention = r5SpeedAndSampleOnly ? false : extraNeedsAttention

  const cardNeedsAttention = !hasSelectedTestType || effectiveExtraNeedsAttention || sampleTypeMissing

  const sequencingCellCount =
    2 + (familySamplesCell ? 1 : 0) + (secondaryDisclosureCell ? 1 : 0)
  const sequencingGridClass =
    sequencingCellCount === 3
      ? 'grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4'
      : 'grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4'

  return (
    <FormCard
      title="Sequencing Options"
      needsAttention={cardNeedsAttention}
      required={required}
      completed={!cardNeedsAttention}
      onEdit={onEdit}
      headerActions={
        onAddComparator ? (
          <button
            type="button"
            onClick={addComparatorDisabled ? undefined : withStopPropagation(onAddComparator)}
            disabled={addComparatorDisabled}
            aria-disabled={addComparatorDisabled}
            title={addComparatorDisabled ? addComparatorDisabledReason : undefined}
            className="inline-flex items-center justify-center gap-1 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-[10px] font-medium text-brand-600 dark:text-brand-400 transition-colors hover:bg-brand-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:border-border-base dark:disabled:border-gray-700 disabled:bg-white dark:disabled:bg-gray-900 disabled:text-text-body dark:disabled:text-gray-400 disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-gray-900 sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs"
          >
            <UserAdd className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            Add Comparator
          </button>
        ) : undefined
      }
      clickableSurface
    >
      {hasSelectedTestType ? (
        <div className={sequencingGridClass}>
          <SequencingGridCell title={testTypeLabel} description={turnaroundDescription} />
          <SequencingGridCell title={sampleTypeDisplay} description={sampleTypeDescription} />
          {familySamplesCell ? (
            <SequencingGridCell title={familySamplesCell.title} description={familySamplesCell.description} />
          ) : null}
          {secondaryDisclosureCell ? (
            <SequencingGridCell title={secondaryDisclosureCell.title} description={secondaryDisclosureCell.description} />
          ) : null}
        </div>
      ) : (
        <p className="text-base font-normal leading-6 text-text-body-subtle dark:text-gray-500">{emptyStateText}</p>
      )}

      <StatusPill
        text={
          !hasSelectedTestType
            ? r5SpeedAndSampleOnly
              ? 'Choose sequencing speed and sample type to place this order'
              : 'Choose sequencing timing, sample type, and comparators to place this order'
            : sampleTypeMissing
              ? 'Choose a sample type to place this order'
              : effectiveExtraNeedsAttention
                ? 'Select Biological Mother or Biological Father as the comparator sample'
                : statusSuccessText
        }
        variant={hasSelectedTestType && !effectiveExtraNeedsAttention && !sampleTypeMissing ? 'success' : 'info'}
      />
    </FormCard>
  )
}
